
import ErrorSnackbar from '@/components/shared/SnackBar';
import FormatterService from "@/service/FormatterService";

const OPENAI_API_KEY = process.env.OPENAI_API_KEY;
const OPENAI_WHISPER_API = process.env.OPENAI_WHISPER_API;
const OPENAI_WHISPER_MODEL = process.env.OPENAI_WHISPER_MODEL;

export default {
    name: 'FinAgentTextInputBar',

    components: {
        ErrorSnackbar,
    },

    props: {
        isCreatingNewMessage: {
            type: Boolean,
            default: false,
        },
        showScrollButton: {
            type: Boolean,
            default: false,
        },
        isProcessingStream: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['send-new-message'],

    data() {
        return {
            content: '',
            isRecording: false,
            isProcessingSpeech: false,
            errorMessage: '',
            recordingTime: 0,
        }
    },

    computed: {
        loadingThreads() {
            return this.$store.state.finAgent.isThreadsLoading;
        },
        isCreatingNewThread() {
            return this.$store.state.finAgent.isCreatingNewThread;
        },
        disabledSending() {
            return !this.content.trim().length
                || this.isCreatingNewMessage
                || this.isProcessingSpeech
                || this.loadingThreads
                || this.isProcessingStream
                || this.isCreatingNewThread;
        },
        showErrorSnackbar() {
            return !!this.errorMessage;
        }
    },

    created() {
        this.format = new FormatterService();
    },

    beforeDestroy() {
        this.reset();
    },

    methods: {
        handleKeyDown(event) {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault(); // prevent auto breakline when pressing Enter

                if (!this.disabledSending) {
                    this.handleSendMessage();
                }
            }
        },

        handleSendMessage() {
            this.$emit('send-new-message', this.content.trim());
            this.content = '';
        },

        handleErrorSnackbarEvent(event) {
            if (!event) {
                this.errorMessage = '';
            }
        },

        async handleSpeechToText(audioChunks) {
            this.isProcessingSpeech = true;
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            const audioFile = new File([audioBlob], "audio.wav", { type: 'audio/wav' });
            const formData = new FormData();
            formData.append('file', audioFile);
            formData.append('model', OPENAI_WHISPER_MODEL);

            try {
                const response = await fetch(OPENAI_WHISPER_API, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${OPENAI_API_KEY}`,
                    },
                    body: formData,
                })

                if (!response.ok) {
                    this.errorMessage = this.$t('component.finAgent.speechToTextError');
                } else {
                    const result = await response.json();
                    this.content = result.text || '';
                }
            } catch (_) {
                this.errorMessage = this.$t('component.finAgent.speechToTextError');
            } finally {
                this.isProcessingSpeech = false;
            }
        },

        increaseRecordingTime() {
            this.recordingTime++;
        },

        async startRecording() {
            this.increaseRecordingTimeInterval = setInterval(this.increaseRecordingTime, 1000);
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const isAllowedRecord = !!stream;
                if (!isAllowedRecord) {
                    return
                }
                this.isRecording = true;
                this.mediaRecorder = new MediaRecorder(stream);
                const audioChunks = [];

                this.mediaRecorder.ondataavailable = event => {
                    audioChunks.push(event.data);
                };

                const handleSpeechToText = this.handleSpeechToText;
                this.mediaRecorder.onstop = async function () {
                    handleSpeechToText(audioChunks);
                };

                this.mediaRecorder.start();
            } catch (_) {
                this.errorMessage = this.$t('component.finAgent.speechToTextError');
            }
        },

        stopRecording() {
            this.isRecording = false;
            this.recordingTime = 0;
            if (this.mediaRecorder) {
                this.mediaRecorder.stop();
            }

            if (this.increaseRecordingTimeInterval) {
                clearInterval(this.increaseRecordingTimeInterval);
                this.increaseRecordingTimeInterval = null;
            }
        },

        reset() {
            this.stopRecording();
            this.isProcessingSpeech = false;
            this.content = '';
        },
    }
}
